// @flow

import * as React from 'react'
import {
  AppBar,
  Toolbar,
  Button,
  Typography,
  Grid,
  CircularProgress,
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'

import HeaderFormsAppEnvironmentsMenu from './HeaderFormsAppEnvironmentsMenu'
import SubmissionHistoryFilters from './SubmissionHistoryFilters'

const useStyles = makeStyles((theme) => ({
  padding: {
    padding: theme.spacing(2),
  },
  logoutLoader: { color: 'white' },
}))

type Props = {
  formId: number | null,
  formsAppEnvironmentId: number,
  loadError: Error | null,
  isLoading: boolean,
  organisations: ExtendedOrganisation[],
  onSelectFormsAppEnvironment: (FormsAppEnvironment) => void,
  onSelectForm: (Form) => void,
  onLogout: () => Promise<void>,
  updateSearch: ({ key: string, value: mixed, persist: boolean }) => void,
  startDate: string,
  endDate: string,
}

function SubmissionHistory({
  loadError,
  isLoading,
  formId,
  formsAppEnvironmentId,
  organisations,
  onSelectFormsAppEnvironment,
  onSelectForm,
  onLogout,
  updateSearch,
  startDate,
  endDate,
}: Props) {
  const classes = useStyles()

  const [loggingOut, setLoggingOut] = React.useState(false)
  const logout = React.useCallback(async () => {
    setLoggingOut(true)
    await onLogout()
  }, [onLogout])

  const integration = React.useMemo(() => {
    for (const organisation of organisations) {
      if (
        organisation.formsAppEnvironments.some(
          (formsAppEnvironment) =>
            formsAppEnvironment.id === formsAppEnvironmentId
        )
      ) {
        return organisation.hcmsIntegration
      }
    }
    return null
  }, [formsAppEnvironmentId, organisations])

  if (loadError) {
    return (
      <div className={classes.padding}>
        <Typography align="center" variant="h2" color="error" paragraph>
          Error Loading Application
        </Typography>
        <Typography align="center" paragraph>
          {loadError.message}
        </Typography>
        <Typography align="center" color="textSecondary">
          Refresh to try again. If the problem persists, please contact support.
        </Typography>
      </div>
    )
  }

  if (isLoading) {
    return (
      <div className={classes.padding}>
        <Grid container justify="center">
          <Grid item>
            <CircularProgress />
          </Grid>
        </Grid>
      </div>
    )
  }

  return (
    <>
      <AppBar position="static">
        <Toolbar>
          <Typography variant="h6">HCMS Submission History</Typography>
          <HeaderFormsAppEnvironmentsMenu
            formsAppEnvironmentId={formsAppEnvironmentId}
            organisations={organisations}
            onSelectFormsAppEnvironment={onSelectFormsAppEnvironment}
          />
          <span style={{ flex: 1 }}></span>
          <Button color="inherit" onClick={logout} disabled={loggingOut}>
            {loggingOut ? (
              <CircularProgress className={classes.logoutLoader} size={25} />
            ) : (
              'Logout'
            )}
          </Button>
        </Toolbar>
      </AppBar>
      <div className={classes.padding}>
        <SubmissionHistoryFilters
          formsAppEnvironmentId={formsAppEnvironmentId}
          formId={formId}
          integration={integration}
          onSelectForm={onSelectForm}
          updateSearch={updateSearch}
          startDate={startDate}
          endDate={endDate}
        />
      </div>
    </>
  )
}

export default React.memo<Props>(SubmissionHistory)
